import { useEffect, useState } from "react";
import { Option } from "tm-shared/types";

interface UseAsyncOptionsParameters<TResponse, TItem> {
    fetchFn: (signal: AbortSignal) => Promise<TResponse>;
    selectData: (response: TResponse) => TItem[];
    mapToOption: (item: TItem) => Option;
    dependencies: unknown[];
    enabled?: boolean;
    debounceMs?: number;
}

interface UseAsyncOptionsResult<TResponse> {
    options: Option[];
    loading: boolean;
    error: Error | null;
    data: TResponse | null;
}

export const useAsyncOptions = <TResponse, TItem>({
    fetchFn,
    selectData,
    mapToOption,
    dependencies,
    enabled = true,
    debounceMs = 0,
}: UseAsyncOptionsParameters<
    TResponse,
    TItem
>): UseAsyncOptionsResult<TResponse> => {
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState<Option[]>([]);
    const [error, setError] = useState<Error | null>(null);
    const [data, setData] = useState<TResponse | null>(null);

    useEffect(() => {
        if (!enabled) {
            setOptions([]);
            setLoading(false);
            setData(null);

            return;
        }

        setLoading(true);

        const timeoutId = setTimeout(() => {
            setOptions([]);
            setError(null);

            const ac = new AbortController();
            let isCurrentRequest = true;

            const fetch = async () => {
                try {
                    const response = await fetchFn(ac.signal);

                    if (!isCurrentRequest) return;

                    setData(response);
                    const items = selectData(response);

                    setOptions(items.map(mapToOption));
                } catch (error_) {
                    if (!isCurrentRequest) return;
                    setError(error_ as Error);
                    setData(null);
                    console.error(error_);
                } finally {
                    if (isCurrentRequest) setLoading(false);
                }
            };

            void fetch();

            return () => {
                isCurrentRequest = false;
                ac.abort();
            };
        }, debounceMs);

        return () => {
            clearTimeout(timeoutId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...dependencies, enabled]);

    return { options, loading, error, data };
};
